// hostport.js — centralized host:port configuration
// ────────────────────────────────────────────────────────────────
// Single source of truth for Admin Server URLs
// ────────────────────────────────────────────────────────────────
//
// Deployed on Vercel: axios calls **same-origin** `/api/admin-server/*` — a serverless
// proxy (`api/admin-server-proxy.js` + vercel rewrite) forwards to Render. External URL rewrites
// on Vercel do not reliably proxy POST. Hitting Render from the browser causes CORS.
//
// Local dev: REACT_APP_ADMIN_SERVER_URL or LOCAL_HOSTPORT → Admin Server (e.g. :2000).
//
// Use getHostport() (via baseURL lazy wrapper) so the URL is resolved when the
// request runs in the browser, not only at module parse time.

export const LOCAL_HOSTPORT = "http://localhost:2000";

export const PROD_HOSTPORT = "https://admin.snapwiz.in";

/** Same-origin path → serverless proxy in `api/admin-server/[...slug].js`. */
export const VERCEL_ADMIN_API_PREFIX = "/api/admin-server";

const normalize = (url) => (url || "").trim().replace(/\/+$/, "");

function isHttpUrl(s) {
  return /^https?:\/\//i.test(s);
}

/** True if URL targets this machine (bad for a deployed UI hitting APIs). */
function isLoopbackApiUrl(s) {
  try {
    const u = new URL(s);
    return u.hostname === "localhost" || u.hostname === "127.0.0.1";
  } catch {
    return false;
  }
}

function useSameOriginAdminProxy() {
  return String(process.env.REACT_APP_ADMIN_USE_SAME_ORIGIN_PROXY || "").toLowerCase() === "true";
}

function resolveHostport() {
  const rawEnv = normalize(process.env.REACT_APP_ADMIN_SERVER_URL);
  let fromEnv = rawEnv && isHttpUrl(rawEnv) ? rawEnv : "";

  if (typeof window !== "undefined" && window.location && window.location.hostname) {
    const host = window.location.hostname;
    const uiIsLoopback = host === "localhost" || host === "127.0.0.1";

    // Same-origin proxy: Vercel → Render (no browser CORS).
    const proxyByEnv = useSameOriginAdminProxy();
    const proxyByVercelHost = host.endsWith(".vercel.app");
    if (!uiIsLoopback && (proxyByEnv || proxyByVercelHost)) {
      return VERCEL_ADMIN_API_PREFIX;
    }

    // Do not use localhost API from a deployed tab (mis-set env on Vercel).
    if (fromEnv && isLoopbackApiUrl(fromEnv) && !uiIsLoopback) {
      fromEnv = "";
    }
    if (fromEnv) return fromEnv;

    if (uiIsLoopback) {
      return LOCAL_HOSTPORT;
    }
  } else if (fromEnv) {
    return fromEnv;
  }

  return process.env.NODE_ENV === "production" ? PROD_HOSTPORT : LOCAL_HOSTPORT;
}

let memo;
/** Resolved once per full page load; call after the browser has a real location. */
export function getHostport() {
  if (memo === undefined) {
    memo = resolveHostport();
  }
  return memo;
}

/**
 * Origin for relative photographer media paths (`profile_pic`, `govt_id_pic`).
 * Set `REACT_APP_SNAPWIZ_MEDIA_BASE_URL` when Admin UI talks to a non-production Backend
 * (e.g. `https://snapwiz-backend-staging.onrender.com`). Defaults to production API.
 */
export function getSnapwizMediaOrigin() {
  const raw = normalize(process.env.REACT_APP_SNAPWIZ_MEDIA_BASE_URL);
  if (raw && isHttpUrl(raw)) return raw.replace(/\/+$/, "");
  return "https://api.snapwiz.in";
}
